
import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ConfirmOptions {
  title: string;
  description: React.ReactNode; // Change from string to ReactNode to support JSX
  onConfirm: () => void;
  onCancel?: () => void;
}

let confirmRef: ((options: ConfirmOptions) => void) | null = null;

export function ConfirmDialog() {
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null);
  const [open, setOpen] = React.useState(false);

  const confirm = React.useCallback((options: ConfirmOptions) => {
    setOptions(options);
    setOpen(true);
  }, []);

  const handleConfirm = React.useCallback(() => {
    if (options?.onConfirm) {
      options.onConfirm();
    }
    setOpen(false);
  }, [options]);

  const handleCancel = React.useCallback(() => {
    if (options?.onCancel) {
      options.onCancel();
    }
    setOpen(false);
  }, [options]);

  React.useEffect(() => {
    confirmRef = confirm;
    return () => {
      confirmRef = null;
    };
  }, [confirm]);

  if (!options) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{options.title}</AlertDialogTitle>
          <AlertDialogDescription>{options.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function confirm(options: ConfirmOptions) {
  if (confirmRef) {
    confirmRef(options);
  } else {
    console.error("ConfirmDialog is not mounted");
  }
}
